/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import _ from 'lodash';
function instanceSort(a, b) {
  const numberOrNotRegex = /\d+|\D+/g;

  // if Sandbox go last
  if (a.environment === 'sandbox' && b.environment !== 'sandbox') {
    return 1;
  } else if (a.environment !== 'sandbox' && b.environment === 'sandbox') {
    return -1;
  }

  // chunk by digit or not digit
  const aChunks = a.key.toUpperCase().match(numberOrNotRegex);
  const bChunks = b.key.toUpperCase().match(numberOrNotRegex);

  // compare each chunk
   
  for (let x = 0; aChunks[x] && bChunks[x]; x++) {
    // skip if the same
     
    if (aChunks[x] === bChunks[x]) continue;

    // cast chunks to numbers
    const aNumber = Number(aChunks[x]);  
    const bNumber = Number(bChunks[x]);  

    // if values were unchanged during casting, then values are numbers
    // test using weak compare
     
    if (aNumber == aChunks[x] && bNumber == bChunks[x]) {
      return aNumber - bNumber;
    }

    // else use simple compare
     
    return aChunks[x] > bChunks[x] ? 1 : -1;
  }

  // if end of chunks reached compare by number of chunks
  return aChunks.length - bChunks.length;
}

// instance: object
// allTags: object
// return: boolean
function filterTagsOnInstance(instance, allTags) {
  let bool = true;
  Object.entries(allTags).forEach(([tagType, tagState]) => {
    // if tags are selected AND
    // the instance does NOT contains selected tags
    if (
      tagState.selectedOptions &&
      tagState.selectedOptions.length &&
      !getInstancesOfTagType(instance, tagType, tagState.selectedOptions)
    ) {
      bool = false;
    }
  });

  return bool;
}

// stack is a subset of tags
// input: object
// return: boolean
function getInstancesOfTagType(instanceObject, tagType, tagOptions) {
  let allTagsStack = false;
  if (instanceObject.Tags && instanceObject.Tags.length) {
    allTagsStack = instanceObject.Tags.reduce(
      // loop through Tags: check { type }
      (accumulator, tag) => {
        return accumulator || (tag.type === tagType && tagOptions.indexOf(tag.value) > -1);
      },
      false
    );

    return allTagsStack;
  }

  return false; // instance without tags ==> instance without stack
}

/**
 * input: array of instance objects
 * return: {
    type1: {
      options: array,
      labelObject: {
        modelName: string,
        modelKey: string,
        modelAttribute: string,
      } ,
      onFilter: function,
      selectedOptions: array,
    },
    tagType2: { ... }
  };
 */
function sortTagsIntoTypes(instanceArr, onFilter) {
  const accumulator = {};

  // aggregate all Tags into one type
  instanceArr.forEach(instance => {
    instance.Tags.forEach(tag => {
      const { value, type } = tag;
      if (Object.keys(accumulator).indexOf(type) === -1) {
        // initialize the key and value
         
        accumulator[type] = {
          options: [value],
          onFilter: onFilter,
          labelObject: {
            modelName: 'tag',
            modelKey: type, // ie. 'stacks'
            modelAttribute: 'label',
          },
          selectedOptions: [],
        };
         
      } else if (accumulator[type].options.indexOf(value) === -1) {
        accumulator[type].options.push(value);  
      }
    });
  });

  // Sort tags' options array 'naturally' (ie. 'Stack 10' > 'Stack 2')
  // TODO: Add 'order' column in tags table to allow flexibility of sort order in future
  const sortedTags = _.mapValues(accumulator, tag => {
    return {
      ...tag,
      options: tag.options.sort(compareAlphaNumericStrings),
    };
  });

  return sortedTags;
}

/**
 * Compares two strings while respecting the numerical portions
 * If a < b returns -1,
 * If a = b returns 0,
 * If a > b returns 1
 *
 * @param {string} a The first string to compare
 * @param {string} b The second string to compare
 * @returns {number} -1,0,1 based on the comparison
 * @example
 *
 * compareAlphaNumericStrings('Stack 1', 'Stack 2')
 * // => -1
 *
 * compareAlphaNumericStrings('Stack 1', 'Stack 1')
 * // => 0
 *
 * compareAlphaNumericStrings('Stack 10', 'Stack 2')
 * // => 1
 */
function compareAlphaNumericStrings(a, b) {
  return a.localeCompare(b, undefined, { numeric: true });
}

/**
 * Given an array of Instances returns unique locations within them
 * @param {object[]} array of instance objects
 * @returns {string[]>} array of unique locations
 */
function getAvailableRegions(instanceArray) {
  const allRegions = _.map(instanceArray, 'location');
  return _.uniq(allRegions);
}

export { sortTagsIntoTypes, getInstancesOfTagType, filterTagsOnInstance, instanceSort, getAvailableRegions };
